/**
 * Port of autro's `packages/core/src/utils/notificationLink.ts` (`notificationUrl` +
 * `withNotificationLink`), minus the mobile deep-link parts — no mobile app here.
 *
 * Turns a notification document into the absolute URL to append to an outgoing email, and gates
 * that footer to staff-like recipients only. The in-app bell is already clickable via
 * `useNavToEvent`; the email carried no URL at all before this.
 */

import { STAFF_LIKE_ACCOUNT_TYPES } from 'constants/optionLists';
import { EVENT_QUERY_PARAM, STAFF_APP_URL, withEventSearch } from 'utils/eventNavigation';

export interface NotificationLinkSource {
  eventId?: string;
  link?: string;
}

export interface StaffLikeContact {
  accountTypes?: string[];
}

export const isStaffLikeContact = (c?: StaffLikeContact | null): boolean =>
  !!c?.accountTypes?.some((t) => (STAFF_LIKE_ACCOUNT_TYPES as readonly string[]).includes(t));

/**
 * Routed through `withEventSearch` so an emailed link lands on exactly the same page as clicking
 * the notification in the bell — `useNavToEvent` does the same thing with the same two fields.
 * Returns '' when the notification has no event to open, so the caller sends the body unchanged
 * rather than linking somewhere ambiguous.
 */
export const notificationUrl = (data: NotificationLinkSource | null | undefined): string => {
  const { eventId, link } = data || {};
  if (!eventId || !link) return '';
  const destination = withEventSearch(link, eventId);
  // withEventSearch is a no-op for non-event paths; no `id` means no event.
  if (!destination.includes(`${EVENT_QUERY_PARAM}=`)) return '';
  return `${STAFF_APP_URL}${destination}`;
};

/**
 * Email-only footer. Never mutates `html` in place at the caller — that string may still back the
 * stored in-app notification / dedupe key. Gated to staff-like recipients: talent contacts have no
 * `/event` route on `talent.onthemove.com`.
 */
export const withNotificationLink = (html = '', data: NotificationLinkSource | null | undefined, recipient?: StaffLikeContact | null): string => {
  if (!isStaffLikeContact(recipient)) return html;
  const url = notificationUrl(data);
  if (!url) return html;
  return `${html}<br><br><a href="${url}">Open this event in OTM</a>`;
};
